import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useLoadScript } from '@react-google-maps/api';

interface InteractiveStreetViewProps {
  apiKey: string;
  initialCoords: { lat: number; lng: number };
  panoId?: string;
  heading?: number;
  pitch?: number;
  onViewChange?: (view: { lat: number; lng: number; heading: number; pitch: number; panorama?: google.maps.StreetViewPanorama }) => void;
}

const libraries: ('geometry')[] = ['geometry'];

const InteractiveStreetView: React.FC<InteractiveStreetViewProps> = ({
  apiKey,
  initialCoords,
  panoId,
  heading = 0,
  pitch = 0,
  onViewChange,
}) => {
  const [pov, setPov] = useState({ heading, pitch });
  const panoramaRef = useRef<google.maps.StreetViewPanorama | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries,
  });

  const handlePovChanged = useCallback(() => {
    if (panoramaRef.current && onViewChange) {
      const currentPov = panoramaRef.current.getPov();
      const position = panoramaRef.current.getPosition();
      if (currentPov && position) {
        const newPov = {
          heading: currentPov.heading || 0,
          pitch: currentPov.pitch || 0
        };
        setPov(newPov);
        onViewChange({
          lat: position.lat(),
          lng: position.lng(),
          heading: newPov.heading,
          pitch: newPov.pitch,
          panorama: panoramaRef.current,
        });
      }
    }
  }, [onViewChange]);

  useEffect(() => {
    if (isLoaded && containerRef.current && !panoramaRef.current) {
      const panorama = new window.google.maps.StreetViewPanorama(
        containerRef.current,
        {
          position: panoId ? undefined : initialCoords,
          pano: panoId,
          pov: { heading, pitch },
          addressControl: false,
          linksControl: true,
          panControl: true,
          enableCloseButton: false,
          showRoadLabels: false,
          motionTracking: false,
          motionTrackingControl: false,
        }
      );

      panoramaRef.current = panorama;
      panorama.addListener('pov_changed', handlePovChanged);
      panorama.addListener('position_changed', handlePovChanged);

      // Initial view change trigger
      handlePovChanged();
    }
  }, [isLoaded, panoId, initialCoords, heading, pitch, handlePovChanged]);

  if (loadError) {
    return (
      <div className="w-full h-64 bg-destructive/10 rounded-lg flex items-center justify-center">
        <p className="text-destructive-foreground">Error loading Google Maps</p>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="w-full h-64 bg-muted rounded-lg flex items-center justify-center">
        <p className="text-muted-foreground">Loading Street View...</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="w-full h-full bg-muted"
      aria-label="Google Street View"
    />
  );
};

export default InteractiveStreetView;
